import {useState, useEffect, useContext} from 'react';
import { AuthContext } from '../contexts/AuthContext';
import {Formik, Form, Field, ErrorMessage} from 'formik'
import {useNavigate} from 'react-router-dom';
import axios from 'axios';
import { Check, ToolTip, Loading, ConfirmationPopup} from './utility/Utility';
import { invalidateAnalyticsSources } from '../hooks/useAnalyticsSources';

export const Shopify = (props) => {
  const {getJwtToken} = useContext(AuthContext);
  const {integration, setIntegration, ret, readOnly = false} = props;
  const [integrating, setIntegrating] = useState(false);
  const [localIntegration, setLocalIntegration] = useState(integration);
  const [channelMappings, setChannelMappings] = useState([]);
  const [availableChannels, setAvailableChannels] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [triggeringBackfill, setTriggeringBackfill] = useState(false);

  useEffect(() => {
    setLocalIntegration(integration);
  }, [integration]);

  useEffect(() => {
    if (ret) setIntegrating(true);
  }, [ret]);

  useEffect(() => {
    // Load available locations from integration
    if (integration && integration.locations) {
      setAvailableLocations(integration.locations);
    }
    // Load available sales channels from integration (shopify_publications)
    if (integration && integration.sales_channels) {
      const channels = integration.sales_channels;
      setAvailableChannels(channels);
      
      // Initialize channel mappings for all channels
      const existingMappings = integration.channel_location_mappings || [];
      const initialMappings = channels.map(channel => {
        const existing = existingMappings.find(m => m.channel === channel);
        return {
          channel,
          locations: existing?.locations || []
        };
      });
      setChannelMappings(initialMappings);
    }
  }, [integration]);

  const integrate = async () => {
    const buttons = [...document.querySelectorAll('.confirmation-popup__buttons button')];
    buttons.forEach(btn => btn.disabled = true);
    try {
      const token = await getJwtToken();
      const res = await axios.post('https://api.amplifyloyalty.com/app/stores/integrations/shopify',
        {_id: ret.ret},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Fire Facebook Pixel StartTrial event
      if (window.fbq) {
        window.fbq('track', 'StartTrial');
      }

      setIntegration(res.data);
      setIntegrating(false);
    } catch (err) {
      console.log(err);
      buttons.forEach(btn => btn.disabled = false);
    }
  }

  const updateChannelMapping = (index, locations) => {
    const updated = [...channelMappings];
    updated[index] = { ...updated[index], locations };
    setChannelMappings(updated);
  };

  const addLocationToChannel = (channelIndex, locationId) => {
    if (!locationId) return;
    const updated = [...channelMappings];
    if (!updated[channelIndex].locations.includes(locationId)) {
      updated[channelIndex].locations = [...updated[channelIndex].locations, locationId];
      setChannelMappings(updated);
    }
  };

  const removeLocationFromChannel = (channelIndex, locationId) => {
    const updated = [...channelMappings];
    updated[channelIndex].locations = updated[channelIndex].locations.filter(id => id !== locationId);
    setChannelMappings(updated);
  };

  const saveSettings = async () => {
    setSaving(true);
    setSaveMessage('');
    try {
      const token = await getJwtToken();
      // Only send mappings that have locations selected
      const mappingsToSave = channelMappings.filter(m => m.locations && m.locations.length > 0);
      const res = await axios.put(
        'https://api.amplifyloyalty.com/app/stores/integrations?integration=shopify',
        { channel_location_mappings: mappingsToSave },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setLocalIntegration(res.data);
      setIntegration(res.data);
      setSaveMessage('Settings saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setSaveMessage(`Error: ${err.response?.data?.message || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const triggerOpportunityBackfill = async () => {
    setTriggeringBackfill(true);
    try {
      const token = await getJwtToken();
      const res = await axios.post(
        'https://api.amplifyloyalty.com/app/product-analysis/opportunity-backfill/enqueue',
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setSaveMessage(res.data.message || 'Opportunity cost backfill triggered successfully!');
      setTimeout(() => setSaveMessage(''), 5000);
    } catch (err) {
      console.error('Error triggering backfill:', err);
      setSaveMessage(`Error triggering backfill: ${err.response?.data?.message || err.message}`);
    } finally {
      setTriggeringBackfill(false);
    }
  };

  const hasChanges = () => {
    // Only compare mappings that have locations (filter out empty ones)
    const currentMappings = (integration?.channel_location_mappings || [])
      .filter(m => m.locations && m.locations.length > 0)
      .sort((a, b) => a.channel.localeCompare(b.channel));
    const modifiedMappings = channelMappings
      .filter(m => m.locations && m.locations.length > 0)
      .sort((a, b) => a.channel.localeCompare(b.channel));
    
    return JSON.stringify(currentMappings) !== JSON.stringify(modifiedMappings);
  };

  return (
    <div className="edit-integration centralize">
      {integrating && 
        <ConfirmationPopup 
          headline="Connect Store"
          description={`Are you sure you want to connect your store ${ret.store} to this account? Only 1 Shopify integration can be active at a time.`}
          action={integrate}
          setActive={setIntegrating}
        />
      }
      <div className="edit-integration__form">
        <div className="edit-integration__form-row edit-integration__form-heading">
          <h2 className='edit-integration__heading'>Shopify</h2>
          {localIntegration.status === 'active' && localIntegration.link === 'active' &&
            <div className="edit-integration__status-row">
              <ToolTip bottom={true} message="To disable integration, uninstall the app from your Shopify store." height="40px" width="145px" />
              <div className="edit-integration__status edit-integration__status--active">Integration is active and working.</div>
            </div>
          }
          {localIntegration.status === 'broken' && 
            <div className="edit-integration__status-row">
              <ToolTip bottom={true} message="To disable integration, uninstall the app from your Shopify store." height="40px" width="145px" />
              <div className="edit-integration__status edit-integration__status--broken">Integration is broken.</div>
            </div>
          }
          {localIntegration.status === 'inactive' && !readOnly &&
            <a href="https://apps.shopify.com/amplify-loyalty" target="_blank" rel="noopener noreferrer" className="btn gradient">Integrate</a>
          }

        </div>
        <div className="edit-integration__form-row">
          <label className="edit-integration__label" htmlFor="store">Store URL</label>
          {localIntegration && 
            <input className="edit-integration__input input--regular" name="store" type="text" value={localIntegration.store} disabled />
          }
          {!localIntegration && <Loading style={{height: "20px", width: "20px"}}/>}
        </div>

        {localIntegration.status === 'active' && !readOnly && (
          <>
            <div className="edit-integration__form-row" style={{marginTop: '30px'}}>
              <h3 style={{marginBottom: '5px'}}>Sales Channel Settings</h3>
            </div>
            
            <div className="edit-integration__form-row">
              <p style={{fontSize: '0.9rem', color: '#666', marginBottom: '15px'}}>
                Map sales channels to fulfillment locations. Opportunity cost is calculated when all mapped locations for a channel are out of stock. Channels with no locations mapped will not calculate opportunity cost.
              </p>
            </div>

            {channelMappings.length === 0 && (
              <div className="edit-integration__form-row">
                <p style={{fontSize: '0.9rem', color: '#666', fontStyle: 'italic'}}>
                  Loading sales channels...
                </p>
              </div>
            )}

            {channelMappings.length > 0 && (
              <div style={{border: '1px solid #e0e0e0', borderRadius: '4px', overflow: 'hidden', marginBottom: '15px'}}>
                {/* Table Header */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '200px 1fr',
                  gap: '15px',
                  padding: '12px 15px',
                  backgroundColor: '#f5f5f5',
                  borderBottom: '1px solid #e0e0e0'
                }}>
                  <div style={{fontSize: '0.75rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600'}}>
                    Sales Channel
                  </div>
                  <div style={{fontSize: '0.75rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600'}}>
                    Fulfillment Locations
                  </div>
                </div>
                
                {/* Table Rows */}
                {channelMappings.map((mapping, index) => (
                  <div 
                    key={mapping.channel} 
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '200px 1fr',
                      gap: '15px',
                      padding: '15px',
                      borderBottom: index < channelMappings.length - 1 ? '1px solid #e0e0e0' : 'none',
                      alignItems: 'start'
                    }}
                  >
                    <div style={{fontSize: '0.95rem', fontWeight: '500'}}>
                      {mapping.channel}
                    </div>
                    <div style={{width: '100%'}}>
                      <select
                        className="edit-integration__input input--regular"
                        value=""
                        onChange={(e) => {
                          addLocationToChannel(index, e.target.value);
                          e.target.value = ''; // Reset dropdown
                        }}
                        style={{width: '100%', maxWidth: '400px'}}
                      >
                        <option value="">Select a location to add...</option>
                        {availableLocations
                          .filter(loc => !mapping.locations.includes(loc.shopify_location_id))
                          .map(loc => (
                            <option key={loc.shopify_location_id} value={loc.shopify_location_id}>
                              {loc.name}
                            </option>
                          ))}
                      </select>
                      {mapping.locations.length > 0 && (
                        <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px'}}>
                          {mapping.locations.map(locationId => {
                            const location = availableLocations.find(loc => loc.shopify_location_id === locationId);
                            return location ? (
                              <span
                                key={locationId}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  padding: '4px 10px',
                                  backgroundColor: '#e3f2fd',
                                  border: '1px solid #90caf9',
                                  borderRadius: '16px',
                                  fontSize: '0.85rem',
                                  color: '#1976d2'
                                }}
                              >
                                {location.name}
                                <button
                                  type="button"
                                  onClick={() => removeLocationFromChannel(index, locationId)}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#1976d2',
                                    cursor: 'pointer',
                                    padding: '0',
                                    marginLeft: '2px',
                                    fontSize: '1rem',
                                    lineHeight: '1',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  ×
                                </button>
                              </span>
                            ) : null;
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="edit-integration__form-row" style={{display: 'flex', justifyContent: 'flex-end', gap: '10px'}}>
              <button
                type="button"
                className="btn gradient"
                onClick={saveSettings}
                disabled={saving || !hasChanges()}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>

            {saveMessage && (
              <div className="edit-integration__form-row" style={{textAlign: 'right'}}>
                <p style={{color: saveMessage.startsWith('Error') ? '#d32f2f' : '#4caf50'}}>
                  {saveMessage}
                </p>
              </div>
            )}

            <div className="edit-integration__form-row" style={{marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #e0e0e0'}}>
              <h3 style={{marginBottom: '5px'}}>Re-run Opportunity Cost Calculation</h3>
            </div>
            
            <div className="edit-integration__form-row">
              <p style={{fontSize: '0.9rem', color: '#666', marginBottom: '15px'}}>
                This will recalculate opportunity cost based on your current channel mappings. At least one channel must have locations mapped to run.
              </p>
            </div>

            <div className="edit-integration__form-row">
              <button
                type="button"
                className="btn gradient"
                onClick={triggerOpportunityBackfill}
                disabled={triggeringBackfill || !channelMappings.some(m => m.locations.length > 0)}
              >
                {triggeringBackfill ? 'Running...' : 'Re-run Opportunity Cost Calculation'}
              </button>
            </div>
          </>
        )}

        {localIntegration.status === 'inactive' &&  
          <div className="edit-integration__integration-description">Press the Integrate button to download our app from the Shopify app store to get started.</div>
        }
        {localIntegration.status === 'broken' && !readOnly &&
          <div className="edit-integration__integration-description">
            <a href="https://apps.shopify.com/amplify-loyalty" target="_blank" rel="noopener noreferrer" className="btn gradient">Reinstall</a>
          </div>
        }
        <div className="edit-integration__tutorial-button-container" style={{marginTop: "10px", display: "flex", justifyContent: "center"}}>
          <a href="/blog/set-up-amplify-loyalty-with-shopify" className="btn gradient">Shopify App Guide</a>
        </div>
      </div>
    </div>
  )
}

const KLAVIYO_ACTIVE_SYNC_STATUSES = ['queued', 'backfilling', 'running'];
// Matches the server's stale cutoff so a dead run doesn't lock the button forever.
const KLAVIYO_SYNC_STALE_MS = 2 * 60 * 60 * 1000;

function isKlaviyoSyncInProgress(sync) {
  if (!sync || !KLAVIYO_ACTIVE_SYNC_STATUSES.includes(sync.status) || sync.error) return false;
  const lastSeen = new Date(sync.last_progress_at || sync.started_at || 0).getTime();
  return Date.now() - lastSeen < KLAVIYO_SYNC_STALE_MS;
}

export const Klaviyo = (props) => {
  const {handleSignOut, getJwtToken} = useContext(AuthContext);
  const {
    integration,
    setIntegration,
    readOnly = false,
    promptReauth = false,
    onReauthPrompted
  } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [metricsError, setMetricsError] = useState('');
  const [savingMetric, setSavingMetric] = useState(false);
  const [reauthPopup, setReauthPopup] = useState(false);
  const [reauthMessage, setReauthMessage] = useState('');
  const [scopesPromptShown, setScopesPromptShown] = useState(false);
  const [resyncPopup, setResyncPopup] = useState(false);
  
  const DEFAULT_SMS_DISCLOSURE = "By opting in, you consent to receive informational (e.g., order updates) and/or marketing texts (e.g., cart reminders) from us including texts sent by autodialer. Consent is not a condition of purchase. Msg & data rates may apply. Msg frequency varies. Unsubscribe at any time by replying STOP or clicking the unsubscribe link (where available). View our {privacy_policy} and {terms}.";
  
  const [values, setValues] = useState({
    name: "Klaviyo",
    privacy_url: "",
    sms_number: "",
    subscribe_message: "",
    terms_url: "",
    sms_disclosure: ""
  });

  const REPORTING_SCOPES = ['campaigns:read', 'flows:read'];
  const grantedScopes = integration?.granted_scopes || [];
  const missingReportingScopes =
    Array.isArray(grantedScopes) &&
    grantedScopes.length > 0 &&
    REPORTING_SCOPES.some((s) => !grantedScopes.includes(s));

  // Reset the form when the saved SMS settings change (not on sync-status polls).
  const hasIntegration = Boolean(integration);
  useEffect(() => {
    if (integration) {
      setIsLoading(false);
      setValues({
        name: "Klaviyo",
        privacy_url: integration.privacy_url || "",
        sms_number: integration.sms_number || "",
        subscribe_message: integration.subscribe_message || "",
        terms_url: integration.terms_url || "",
        sms_disclosure: integration.sms_disclosure || DEFAULT_SMS_DISCLOSURE
      });
    }
  }, [
    hasIntegration,
    integration?.privacy_url,
    integration?.sms_number,
    integration?.subscribe_message,
    integration?.terms_url,
    integration?.sms_disclosure
  ])

  useEffect(() => {
    if (!promptReauth) return;
    setReauthMessage(
      'Analytics sync needs updated Klaviyo permissions for campaigns and flows. Confirm to reconnect and approve the additional access — your SMS settings will be kept. If this keeps happening, enable campaigns:read and flows:read on the Amplify Klaviyo app in Klaviyo’s developer portal.'
    );
    setReauthPopup(true);
    setScopesPromptShown(true);
    onReauthPrompted?.();
  }, [promptReauth, onReauthPrompted]);

  // Surface missing reporting scopes even when OAuth redirected as "success"
  // (Klaviyo silently omits scopes that are not enabled on the OAuth app).
  useEffect(() => {
    if (!missingReportingScopes || readOnly || scopesPromptShown) return;
    setReauthMessage(
      'Klaviyo is connected, but campaigns:read and flows:read were not granted. Add those scopes on the Amplify app in Klaviyo’s developer portal, then confirm reconnect and approve the updated permissions.'
    );
    setReauthPopup(true);
    setScopesPromptShown(true);
  }, [missingReportingScopes, readOnly, scopesPromptShown]);

  useEffect(() => {
    if (!(integration?.status === 'active' && integration?.link === 'active')) {
      setMetrics(null);
      setMetricsError('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = await getJwtToken();
        const res = await axios.get(
          'https://api.amplifyloyalty.com/app/stores/integrations/klaviyo/metrics',
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) {
          setMetrics(res.data?.metrics || []);
          setMetricsError('');
        }
      } catch (err) {
        if (!cancelled) {
          setMetrics([]);
          setMetricsError(err.response?.data?.message || 'Could not load Klaviyo metrics.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [integration?.status, integration?.link, getJwtToken]);

  const pollSync = isKlaviyoSyncInProgress(integration?.sync);
  useEffect(() => {
    if (!pollSync) return;
    let cancelled = false;
    const id = setInterval(async () => {
      try {
        const token = await getJwtToken();
        const res = await axios.get('https://api.amplifyloyalty.com/app/stores/integrations', {
          headers: { Authorization: `Bearer ${token}` },
          params: { integration: 'klaviyo' }
        });
        if (cancelled || !res.data || res.data.name === 'BadRequestError') return;
        // Only merge sync/status so in-progress SMS form edits are not clobbered.
        setIntegration((prev) => ({
          ...prev,
          status: res.data.status ?? prev?.status,
          sync: res.data.sync ?? prev?.sync
        }));
      } catch (err) {
        console.error('Failed to refresh Klaviyo sync status', err);
      }
    }, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [pollSync, getJwtToken, setIntegration]);

  const checkChanges = () => {
    const buttons = document.querySelectorAll('.klaviyo-save-button');
    if (buttons.length === 0) return;
    
    let different = false;
    for (let key of Object.keys(values)) {
      if (integration && integration[key] != values[key]) {
        different = true;
        break;
      }
    }
    buttons.forEach(button => {
      button.disabled = !different;
    });
  }

  useEffect(() => {
    checkChanges();
  }, [values])

  const updateValue = (key, value) => {
    setValues({...values, [key]: value});
  }

  const resetDisclosureToDefault = () => {
    setValues({...values, sms_disclosure: DEFAULT_SMS_DISCLOSURE});
  }

  const handleInitiateAuth = async () => {
    setIsLoading(true);
    try {
      const token = await getJwtToken();
      const response = await axios.post(
        'https://api.amplifyloyalty.com/app/stores/integrations/klaviyo/initiate',
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      if (response.data.name === 'BadRequestError') {
        handleSignOut();
      }
      // Redirect the user to the Klaviyo authorization URL
      window.location.href = response.data.authorizationUrl;
    } catch (error) {
      console.error('Error initiating Klaviyo auth:', error);
      alert('There was an error connecting to Klaviyo. Please try again.');
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect from Klaviyo? This will stop all Klaviyo integrations.')) {
      return;
    }
    setIsLoading(true);
    try {
      const token = await getJwtToken();
      await axios.delete(
        'https://api.amplifyloyalty.com/app/stores/integrations/klaviyo',
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      // Tell the parent component to refresh the integration data
      setIntegration({...integration, status: 'inactive', private_key: ''});
      invalidateAnalyticsSources();
    } catch (err) {
      console.error("Failed to disconnect Klaviyo", err);
      alert('Failed to disconnect. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConversionMetricChange = async (metricId) => {
    const selected = (metrics || []).find((m) => m.id === metricId);
    if (!metricId || !selected) return;
    if (metricId === integration?.conversion_metric_id) return;
    setSavingMetric(true);
    try {
      const token = await getJwtToken();
      const res = await axios.put(
        'https://api.amplifyloyalty.com/app/stores/integrations',
        {
          conversion_metric_id: selected.id,
          conversion_metric_name: selected.name
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { integration: 'klaviyo' }
        }
      );
      if (res.data.name === 'BadRequestError') {
        handleSignOut();
        return;
      }
      setIntegration((prev) => ({
        ...prev,
        ...res.data,
        conversion_metric_id: selected.id,
        conversion_metric_name: selected.name
      }));
    } catch (err) {
      console.error('Failed to save conversion metric', err);
      alert(err.response?.data?.message || 'Failed to save conversion metric.');
    } finally {
      setSavingMetric(false);
    }
  };

  const handleStartAnalyticsSync = async () => {
    setIsLoading(true);
    try {
      const token = await getJwtToken();
      const res = await axios.post(
        'https://api.amplifyloyalty.com/app/stores/integrations/klaviyo/sync',
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIntegration((prev) => ({
        ...prev,
        sync: res.data?.sync || prev?.sync,
        conversion_metric_id: res.data?.conversion_metric_id || prev?.conversion_metric_id,
        conversion_metric_name: res.data?.conversion_metric_name || prev?.conversion_metric_name
      }));
      invalidateAnalyticsSources();
    } catch (err) {
      console.error('Failed to start Klaviyo analytics sync', err);
      const apiMessage = err.response?.data?.message || '';
      if (/already in progress/i.test(apiMessage)) {
        // Another tab or the OAuth callback started a run; show it as in progress instead of a reauth prompt.
        setIntegration((prev) => ({
          ...prev,
          sync: { ...(prev?.sync || {}), status: 'backfilling', error: null, last_progress_at: new Date().toISOString() }
        }));
        return;
      }
      const needsScopes = /campaigns:read|flows:read|reconnect/i.test(apiMessage);
      setReauthMessage(
        needsScopes
          ? 'Analytics sync needs updated Klaviyo permissions for campaigns and flows. Confirm to reconnect and approve the additional access — your SMS settings will be kept.'
          : (apiMessage || 'Failed to start analytics sync. Confirm to reconnect Klaviyo and try again.')
      );
      setReauthPopup(true);
    } finally {
      setIsLoading(false);
    }
  };

  const syncStatus = integration?.sync?.status || 'idle';
  const syncInProgress = isKlaviyoSyncInProgress(integration?.sync);
  const hasSynced = syncStatus === 'completed' && !integration?.sync?.error && !missingReportingScopes;
  const syncLive =
    !missingReportingScopes &&
    ['queued', 'backfilling', 'running', 'completed'].includes(syncStatus);
  const syncProgress =
    integration?.sync?.chunks_total
      ? ` (${integration.sync.chunks_processed || 0}/${integration.sync.chunks_total})`
      : '';
  let analyticsStatusText = 'Klaviyo analytics is not syncing.';
  if (missingReportingScopes) {
    analyticsStatusText =
      'Klaviyo analytics needs campaigns:read and flows:read. Enable those scopes on the Amplify Klaviyo app, then reconnect.';
  } else if (syncStatus === 'queued') {
    analyticsStatusText = 'Klaviyo analytics sync is queued and will start shortly.';
  } else if (syncStatus === 'backfilling' || syncStatus === 'running') {
    analyticsStatusText = `Klaviyo analytics sync is live and syncing${syncProgress}.`;
  } else if (syncStatus === 'completed') {
    analyticsStatusText = 'Klaviyo analytics is connected and syncing.';
  } else if (syncStatus === 'error' || integration?.sync?.error) {
    analyticsStatusText = integration?.sync?.error
      ? `Klaviyo analytics sync failed: ${integration.sync.error}`
      : 'Klaviyo analytics sync failed.';
  } else if (!integration?.conversion_metric_id) {
    analyticsStatusText = 'Select a conversion metric to enable analytics sync.';
  }
  
  const submit = async () => {
    // Validate sms_disclosure is not empty
    if (!values.sms_disclosure || values.sms_disclosure.trim() === '') {
      alert('SMS Opt-In Disclosure is required. Please fill it in or click "Reset to Default".');
      return;
    }
    
    setSubmitting(true);
    let inputs = [...document.querySelectorAll('.edit-integration__form input'), document.querySelector('.edit-integration__submit')];
    inputs.forEach(input => input.disabled = true);
    
    const tags = ['email'];
    if (values.sms_number && values.subscribe_message && values.terms_url && values.privacy_url && values.sms_disclosure) {
      tags.push('texts');
    }
    
    let payload = {
      ...values,
      tags
    };
    
    try {
      const token = await getJwtToken();
      const res = await axios.put('https://api.amplifyloyalty.com/app/stores/integrations',
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            integration: 'klaviyo'
          }
        }
      );
      if (res.data.name === 'BadRequestError') {
        handleSignOut();
      } else {
        setIntegration({...res.data});
      }
    } catch (err) {
      console.log(err);
      alert('Failed to save settings. Please try again.');
    } finally {
      inputs.forEach(input => input.disabled = false);
      setSubmitting(false);
    }
  }

  return (
    <section className="edit-integration centralize">
      {reauthPopup && (
        <ConfirmationPopup
          headline="Reconnect Klaviyo"
          description={
            reauthMessage
            || 'Analytics sync needs updated Klaviyo permissions. Confirm to reconnect and grant access to campaigns and flows.'
          }
          action={handleInitiateAuth}
          setActive={setReauthPopup}
        />
      )}
      {resyncPopup && (
        <ConfirmationPopup
          headline="Re-sync Klaviyo analytics?"
          description="This re-pulls the last 24 months of campaign and flow performance from Klaviyo and overwrites the analytics data already synced. It may take several minutes to complete, and you won't be able to start another sync until it finishes."
          action={() => {
            setResyncPopup(false);
            handleStartAnalyticsSync();
          }}
          setActive={setResyncPopup}
        />
      )}
      <div className='edit-integration__form'>
        <div className="edit-integration__form-row">
          <h2 className='edit-integration__heading'>Klaviyo</h2>
        </div>
        
        {integration && integration.status === 'active' && integration.link === "active" && (
          <>
            <div className="edit-integration__status edit-integration__status--active edit-integration__status--row">
              <div className="edit-integration__status-text">The Klaviyo integration is connected and active.</div>
            </div>

            <div className="edit-integration__divider">
              <div className="edit-integration__divider-title">Analytics Sync</div>
              <ToolTip
                message="Pulls campaign and flow performance for the Product Analysis Klaviyo slide. Reconnect if you connected before campaigns:read / flows:read were added."
                width="220px"
                align="left"
              />
            </div>
            <div
              className={`edit-integration__status edit-integration__status--row ${
                syncLive
                  ? 'edit-integration__status--active'
                  : 'edit-integration__status--broken'
              }`}
            >
              <div className="edit-integration__status-text">{analyticsStatusText}</div>
            </div>
            <div className="edit-integration__form-row">
              <label className="edit-integration__label" htmlFor="klaviyo_conversion_metric">
                Conversion metric
              </label>
              {metricsError ? (
                <div className="edit-integration__warning">{metricsError}</div>
              ) : (
                <select
                  id="klaviyo_conversion_metric"
                  className="edit-integration__input input--regular"
                  value={integration.conversion_metric_id || ''}
                  disabled={readOnly || savingMetric || !metrics}
                  onChange={(e) => handleConversionMetricChange(e.target.value)}
                >
                  <option value="">
                    {metrics ? 'Select a conversion metric' : 'Loading metrics…'}
                  </option>
                  {(metrics || []).map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                  {integration.conversion_metric_id &&
                    !(metrics || []).some((m) => m.id === integration.conversion_metric_id) && (
                      <option value={integration.conversion_metric_id}>
                        {integration.conversion_metric_name || integration.conversion_metric_id}
                      </option>
                    )}
                </select>
              )}
            </div>
            {!readOnly && (
              <div className="edit-integration__form-buttons" style={{ marginBottom: 15 }}>
                <button
                  type="button"
                  className="edit-integration__submit btn gradient"
                  onClick={hasSynced ? () => setResyncPopup(true) : handleStartAnalyticsSync}
                  disabled={
                    isLoading || submitting || savingMetric || syncInProgress || !integration.conversion_metric_id
                  }
                  title={syncInProgress ? 'A sync is already running. You can start another once it finishes.' : undefined}
                >
                  {isLoading
                    ? 'Starting…'
                    : syncInProgress
                      ? 'Sync in progress…'
                      : hasSynced
                        ? 'Re-sync analytics data'
                        : 'Sync analytics data'}
                </button>
              </div>
            )}

            <div className="edit-integration__divider">
              <div className="edit-integration__divider-title">SMS Settings (Optional)</div>
              <ToolTip 
                message="SMS settings are optional, but all fields must be completed for Klaviyo to be an option for SMS integration in your Program." 
                width="200px"
                align="left"
              />
            </div>
            
            {/* Show warning if SMS fields are partially filled */}
            {!(values.sms_number && values.subscribe_message && values.terms_url && values.privacy_url && values.sms_disclosure) && (
              <div className="edit-integration__warning">
                <i className="fa-solid fa-triangle-exclamation"></i>
                To utilize Klaviyo as an SMS integration, you must complete all SMS fields including the opt-in disclosure.
              </div>
            )}
            
            <div className="edit-integration__form-row">
              <label className="edit-integration__label" htmlFor="sms_number">SMS Number</label>
              <input 
                className="edit-integration__input input--regular" 
                name="sms_number" 
                type="tel" 
                placeholder="+11234567891" 
                value={values.sms_number} 
                onChange={(e) => updateValue("sms_number", e.target.value)}
                disabled={submitting}
              />
            </div>
            
            <div className="edit-integration__form-row">
              <div className="edit-integration__label-container">
                <label className="edit-integration__label" htmlFor="subscribe_message">SMS Subscribe Keyword</label>
                <ToolTip
                  message="The message that a subscriber will send to your number to subscribe to marketing texts. This message must include keywords that are set to opt-in a user in Klaviyo SMS settings."
                  width="200px"
                />
              </div>
              <input 
                className="edit-integration__input input--regular" 
                name="subscribe_message" 
                type="text" 
                placeholder="JOIN" 
                value={values.subscribe_message} 
                onChange={(e) => updateValue("subscribe_message", e.target.value)}
                disabled={submitting}
              />
            </div>
            
            <div className="edit-integration__form-row">
              <div className="edit-integration__label-container">
                <label className="edit-integration__label" htmlFor="terms_url">Mobile Terms URL</label>
                <ToolTip
                  message={`This must be specific to mobile terms of service.`}
                  width="200px"  
                />
              </div>
              <input 
                className="edit-integration__input input--regular" 
                name="terms_url" 
                type="text" 
                placeholder="https://mysite.com/terms-and-conditions" 
                value={values.terms_url} 
                onChange={(e) => updateValue("terms_url", e.target.value)}
                disabled={submitting}
              />
            </div>
            
            <div className="edit-integration__form-row">
              <div className="edit-integration__label-container">
                <label className="edit-integration__label" htmlFor="privacy_url">Privacy Policy URL</label>
              </div>
              <input 
                className="edit-integration__input input--regular" 
                name="privacy_url" 
                type="text" 
                placeholder="https://mysite.com/privacy-policy" 
                value={values.privacy_url} 
                onChange={(e) => updateValue("privacy_url", e.target.value)}
                disabled={submitting}
              />
            </div>
            
            <div className="edit-integration__form-row-full">
              <div className="edit-integration__label-container" style={{ justifyContent: 'space-between', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label className="edit-integration__label" htmlFor="sms_disclosure">SMS Opt-In Disclosure*</label>
                  <ToolTip
                    message="This disclosure will be shown to customers when they opt-in to SMS. Use {privacy_policy} and {terms} as placeholders that will automatically link to your Privacy Policy URL and Terms & Conditions URL. Customize to match your business and ensure legal compliance. This field is required."
                    width="300px"  
                  />
                </div>
                <button 
                  type="button"
                  onClick={resetDisclosureToDefault}
                  className="edit-integration__reset-button"
                  disabled={submitting}
                >
                  Reset to Default
                </button>
              </div>
              <textarea 
                className="edit-integration__input edit-integration__textarea" 
                name="sms_disclosure" 
                rows="4"
                placeholder="By opting in, you consent to receive marketing texts..."
                value={values.sms_disclosure} 
                onChange={(e) => updateValue("sms_disclosure", e.target.value)}
                disabled={submitting}
                required
              />
            </div>
            
            {/* Legal Disclaimer */}
            <div className="edit-integration__disclaimer">
              <strong>Legal Disclaimer:</strong> By using this integration, you acknowledge that you are solely responsible for compliance with all applicable laws and regulations, including but not limited to SMS marketing laws (TCPA, CAN-SPAM, GDPR, etc.). Amplify Loyalty is not responsible for any legal issues, fines, or damages that may arise from your use of this integration or your marketing practices. You agree to obtain proper consent from recipients and follow all relevant regulations.
            </div>
            
            {!readOnly && (
            <div className="edit-integration__form-buttons">
              <button 
                className="edit-integration__submit btn" 
                onClick={handleDisconnect} 
                disabled={isLoading || submitting}
              >
                Disconnect
              </button>
              <button 
                className="edit-integration__submit klaviyo-save-button btn gradient" 
                onClick={submit}
                disabled={submitting}
                style={{marginLeft: "10px"}}
              >
                Save
              </button>
            </div>
            )}
          </>
        )}
        
        {integration &&
          ((integration.status === 'active' && integration.link !== 'active') ||
            integration.status === 'needs_reauth') && (
          <>
            <div className="edit-integration__status edit-integration__status--broken edit-integration__status--row">
              <div className="edit-integration__status-text">
                {integration.status === 'needs_reauth'
                  ? 'Klaviyo authorization expired. Please reconnect to resume syncing.'
                  : 'The Klaviyo integration is broken. Please reconnect.'}
              </div>
            </div>
            {integration.sync?.error && (
              <div className="edit-integration__warning">{integration.sync.error}</div>
            )}
            {!readOnly && (
            <div className="edit-integration__form-buttons">
              <button 
                className="edit-integration__submit btn gradient" 
                onClick={handleInitiateAuth} 
                disabled={isLoading}
              >
                {isLoading ? 'Connecting...' : 'Reconnect'}
              </button>
              <button 
                className="edit-integration__submit btn" 
                onClick={handleDisconnect} 
                disabled={isLoading}
              >
                Disconnect
              </button>
            </div>
            )}
          </>
        )}
        
        {integration && !['active', 'needs_reauth'].includes(integration.status) && (
          <>
            <div className="edit-integration__status edit-integration__status--inactive edit-integration__status--row">
              <div className="edit-integration__status-text">Connect your Klaviyo account to sync customer profiles and enable email/SMS integrations.</div>
            </div>
            {!readOnly && (
            <div className="edit-integration__form-buttons">
              <button 
                className="edit-integration__submit btn gradient" 
                onClick={handleInitiateAuth} 
                disabled={isLoading}
              >
                {isLoading ? 'Connecting...' : 'Connect Klaviyo'}
              </button>
            </div>
            )}
          </>
        )}
        
        {!integration && <Loading />}
      </div>
    </section>
  )
}

export const JudgeMe = (props) => {
  const { integration, setIntegration, readOnly = false } = props;
  const [isLoading, setIsLoading] = useState(false);
  const { handleSignOut, getJwtToken } = useContext(AuthContext);

  useEffect(() => {
    console.log('integration', integration);
  }, [integration]);

  const handleInitiateAuth = async () => {
    setIsLoading(true);
    try {
      const token = await getJwtToken();
      const response = await axios.post(
        'https://api.amplifyloyalty.com/app/stores/integrations/judgeme/initiate',
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      if (response.data.name === 'BadRequestError') {
        handleSignOut();
      }
      // Redirect the user to the Judge.me authorization URL
      window.location.href = response.data.authorizationUrl;
    } catch (error) {
      console.error('Error initiating Judge.me auth:', error);
      alert('There was an error connecting to Judge.me. Please try again.');
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect from Judge.me? This will stop points from being awarded for new reviews.')) {
      return;
    }
    setIsLoading(true);
    try {
      const token = await getJwtToken();
      await axios.delete(
        'https://api.amplifyloyalty.com/app/stores/integrations/judgeme',
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      // Tell the parent component to refresh the integration data
      setIntegration({...integration, status: 'inactive'});
    } catch (err) {
      console.error("Failed to disconnect Judge.me", err);
      alert('Failed to disconnect. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="edit-integration centralize">
      <div className="edit-integration__form-row">
        <h2 className='edit-integration__heading'>Judge.me</h2>
      </div>
      {integration.status === 'active' && integration.link !== 'broken' &&
        <>
          <div className="edit-integration__status edit-integration__status--active edit-integration__status--row">
            <div className="edit-integration__status-text">The Judge.me integration is sending and receiving data.</div>
          </div>
          {!readOnly && (
          <div className="edit-integration__form-buttons">
            <button className="edit-integration__submit btn" onClick={handleDisconnect} disabled={isLoading}>Disconnect</button>
          </div>
          )}
        </>
      }
      {integration.status === 'active' && integration.link === 'broken' &&
        <>
          <div className="edit-integration__status edit-integration__status--broken edit-integration__status--row">
            <div className="edit-integration__status-text">{integration.message}</div>
          </div>
          {!readOnly && (
          <div className="edit-integration__form-buttons">
            <button className="edit-integration__submit btn" onClick={handleDisconnect} disabled={isLoading}>Disconnect</button>
          </div>
          )}
        </>
      }
      {integration.status === 'inactive' &&
        <>
          <div className="edit-integration__status edit-integration__status--inactive edit-integration__status--row">
            <div className="edit-integration__status-text">You <strong>must</strong> be on the Awesome plan in your Judge.me account to use this integration.</div>
          </div>
          {!readOnly && (
          <div className="edit-integration__form-buttons">
            <button className="edit-integration__submit btn" onClick={handleInitiateAuth} disabled={isLoading}>Integrate</button>
          </div>
          )}
        </>
      }
    </section>
  )
};
const META_API = 'https://api.amplifyloyalty.com/app/stores/integrations';
const META_CLICK_WINDOWS = [
  { id: '1d_click', label: '1-day click' },
  { id: '7d_click', label: '7-day click' }
];
const META_VIEW_WINDOWS = [
  { id: 'none', label: 'No view-through' },
  { id: '1d_view', label: '1-day view' }
];
const META_SYNC_RUNNING = new Set(['queued', 'backfilling', 'running']);

function formatMetaDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString() : '—';
}

export const MetaAds = (props) => {
  const { integration, setIntegration, readOnly = false } = props;
  const { handleSignOut, getJwtToken } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [accounts, setAccounts] = useState(null);
  const [accountsError, setAccountsError] = useState('');
  const [form, setForm] = useState({ ad_account_id: '', click_window: '7d_click', view_window: '1d_view' });

  const link = integration?.link || 'inactive';
  const status = integration?.status || 'inactive';
  const sync = integration?.sync || {};
  const syncRunning = META_SYNC_RUNNING.has(sync.status);

  useEffect(() => {
    if (!integration) return;
    setForm({
      ad_account_id: integration.ad_account?.id || '',
      click_window: integration.settings?.click_window || '7d_click',
      view_window: integration.settings?.view_window || '1d_view'
    });
  }, [integration]);

  useEffect(() => {
    if (status !== 'pending_setup' || link === 'needs_reauth' || accounts) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getJwtToken();
        const res = await axios.get(`${META_API}/meta-ads/ad-accounts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (cancelled) return;
        const list = Array.isArray(res.data?.accounts) ? res.data.accounts : [];
        setAccounts(list);
        if (list.length === 1) setForm((f) => (f.ad_account_id ? f : { ...f, ad_account_id: list[0].id }));
      } catch (err) {
        if (!cancelled) setAccountsError(err.response?.data?.message || 'Could not load ad accounts.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status, link, accounts, getJwtToken]);

  // Poll while a backfill runs so progress updates without a page refresh.
  useEffect(() => {
    if (!syncRunning) return;
    const timer = setInterval(async () => {
      try {
        const token = await getJwtToken();
        const res = await axios.get(META_API, {
          headers: { Authorization: `Bearer ${token}` },
          params: { integration: 'meta-ads' }
        });
        if (res.data && res.data.name !== 'BadRequestError') setIntegration(res.data);
      } catch (err) {
        console.log(err);
      }
    }, 10000);
    return () => clearInterval(timer);
  }, [syncRunning, getJwtToken, setIntegration]);

  const authHeaders = async () => ({ headers: { Authorization: `Bearer ${await getJwtToken()}` } });

  const handleConnect = async () => {
    setIsLoading(true);
    try {
      const res = await axios.post(`${META_API}/meta-ads/initiate`, {}, await authHeaders());
      if (res.data.name === 'BadRequestError') return handleSignOut();
      window.location.href = res.data.authorizationUrl;
    } catch (err) {
      console.error('Error initiating Meta auth:', err);
      alert('There was an error connecting to Facebook. Please try again.');
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Meta Ads? Syncing will stop. Previously synced data is kept.')) return;
    setIsLoading(true);
    try {
      const res = await axios.delete(`${META_API}/meta-ads`, await authHeaders());
      setIntegration(res.data);
      setAccounts(null);
      invalidateAnalyticsSources();
    } catch (err) {
      console.error('Failed to disconnect Meta Ads', err);
      alert('Failed to disconnect. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = async (payload) => {
    const res = await axios.put(META_API, payload, {
      ...(await authHeaders()),
      params: { integration: 'meta-ads' }
    });
    return res.data;
  };

  const handleStartSync = async () => {
    if (!form.ad_account_id) {
      alert('Select an ad account first.');
      return;
    }
    setIsLoading(true);
    try {
      await saveSettings(form);
      const res = await axios.post(`${META_API}/meta-ads/sync`, {}, await authHeaders());
      setIntegration(res.data);
      invalidateAnalyticsSources();
    } catch (err) {
      console.error('Failed to start Meta Ads sync', err);
      alert(err.response?.data?.message || 'Failed to start the sync. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const attributionDirty =
    form.click_window !== (integration?.settings?.click_window || '7d_click') ||
    form.view_window !== (integration?.settings?.view_window || '1d_view');

  const handleSaveAttribution = async () => {
    if (
      !window.confirm(
        'Changing attribution re-pulls all historical Meta Ads data with the new windows. This can take a while. Continue?'
      )
    ) {
      return;
    }
    setIsLoading(true);
    try {
      setIntegration(await saveSettings({ click_window: form.click_window, view_window: form.view_window }));
    } catch (err) {
      console.error('Failed to save Meta Ads settings', err);
      alert(err.response?.data?.message || 'Failed to save settings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const attributionFields = (disabled) => (
    <>
      <div className="edit-integration__form-row">
        <label className="edit-integration__label" htmlFor="meta_click_window">Click attribution</label>
        <select
          id="meta_click_window"
          className="edit-integration__input input--regular"
          value={form.click_window}
          disabled={disabled}
          onChange={(e) => setForm({ ...form, click_window: e.target.value })}
        >
          {META_CLICK_WINDOWS.map((o) => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </select>
      </div>
      <div className="edit-integration__form-row">
        <label className="edit-integration__label" htmlFor="meta_view_window">View attribution</label>
        <select
          id="meta_view_window"
          className="edit-integration__input input--regular"
          value={form.view_window}
          disabled={disabled}
          onChange={(e) => setForm({ ...form, view_window: e.target.value })}
        >
          {META_VIEW_WINDOWS.map((o) => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </select>
      </div>
    </>
  );

  if (!integration) return <Loading />;

  return (
    <section className="edit-integration centralize">
      <div className="edit-integration__form-row">
        <h2 className="edit-integration__heading">Meta Ads</h2>
      </div>

      {(link === 'needs_reauth' || integration.reauth_soon) && status !== 'inactive' && (
        <>
          <div className="edit-integration__status edit-integration__status--broken edit-integration__status--row">
            <div className="edit-integration__status-text">
              {link === 'needs_reauth'
                ? 'Your Facebook connection has expired or was revoked. Reconnect to resume syncing.'
                : `Facebook will stop sharing data on ${formatMetaDate(integration.data_access_expires_at)} unless you reconnect.`}
            </div>
          </div>
          {!readOnly && (
            <div className="edit-integration__form-buttons">
              <button className="edit-integration__submit btn" onClick={handleConnect} disabled={isLoading}>
                Reconnect Facebook
              </button>
            </div>
          )}
        </>
      )}

      {status === 'inactive' && (
        <>
          <div className="edit-integration__status edit-integration__status--inactive edit-integration__status--row">
            <div className="edit-integration__status-text">
              Connect your Facebook ads account to begin analyzing campaigns.
            </div>
          </div>
          {!readOnly && (
            <div className="edit-integration__form-buttons">
              <button className="edit-integration__submit btn" onClick={handleConnect} disabled={isLoading}>
                Connect with Facebook
              </button>
            </div>
          )}
        </>
      )}

      {status === 'pending_setup' && link !== 'needs_reauth' && (
        <div className="edit-integration__form">
          <div className="edit-integration__status edit-integration__status--active edit-integration__status--row">
            <div className="edit-integration__status-text">
              Connected as <strong>{integration.fb_user_name || 'Facebook user'}</strong>. Choose an ad account and
              attribution before pulling historical data.
            </div>
          </div>
          <div className="edit-integration__form-row">
            <label className="edit-integration__label" htmlFor="meta_ad_account">Ad account</label>
            {accountsError ? (
              <div className="edit-integration__warning">{accountsError}</div>
            ) : (
              <select
                id="meta_ad_account"
                className="edit-integration__input input--regular"
                value={form.ad_account_id}
                disabled={readOnly || !accounts}
                onChange={(e) => setForm({ ...form, ad_account_id: e.target.value })}
              >
                <option value="">{accounts ? 'Select an ad account' : 'Loading ad accounts…'}</option>
                {(accounts || []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                    {a.business_name ? ` — ${a.business_name}` : ''} ({a.currency})
                  </option>
                ))}
              </select>
            )}
          </div>
          {attributionFields(readOnly)}
          {!readOnly && (
            <div className="edit-integration__form-buttons">
              <button className="edit-integration__submit btn" onClick={handleDisconnect} disabled={isLoading}>
                Disconnect
              </button>
              <button
                className="edit-integration__submit btn gradient"
                onClick={handleStartSync}
                disabled={isLoading || !form.ad_account_id}
                style={{ marginLeft: '10px' }}
              >
                Sync Data
              </button>
            </div>
          )}
        </div>
      )}

      {status === 'active' && link !== 'needs_reauth' && (
        <div className="edit-integration__form">
          {!integration.reauth_soon && (
            <div className="edit-integration__status edit-integration__status--active edit-integration__status--row">
              <div className="edit-integration__status-text">
                Syncing <strong>{integration.ad_account?.name}</strong>
                {integration.ad_account?.business_name ? ` (${integration.ad_account.business_name})` : ''} ·{' '}
                {integration.ad_account?.currency}
              </div>
            </div>
          )}
          <div className="edit-integration__form-row">
            <label className="edit-integration__label">Sync status</label>
            <div>
              {syncRunning
                ? `Backfilling ${
                    sync.chunks_total
                      ? `${Math.min(100, Math.round(((sync.chunks_processed || 0) / sync.chunks_total) * 100))}%`
                      : '…'
                  }`
                : sync.status === 'completed'
                  ? `Up to date through ${sync.last_insights_ymd || '—'}`
                  : sync.status === 'error'
                    ? 'Sync error'
                    : 'Idle'}
              {sync.error && <div className="edit-integration__warning">{sync.error}</div>}
            </div>
          </div>
          {attributionFields(readOnly || isLoading || syncRunning)}
          {!readOnly && (
            <div className="edit-integration__form-buttons">
              <button className="edit-integration__submit btn" onClick={handleDisconnect} disabled={isLoading}>
                Disconnect
              </button>
              <button
                className="edit-integration__submit btn gradient"
                onClick={handleSaveAttribution}
                disabled={isLoading || !attributionDirty || syncRunning}
                style={{ marginLeft: '10px' }}
              >
                Save attribution
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
